import { useState, useEffect } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';
import { getQueuedTickets, processQueue } from '../utils/offlineSync';

export default function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [queuedCount, setQueuedCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    // DRAIN WHENEVER WE ARE ONLINE AND SOMETHING IS WAITING, not only on the browser's `online`
    // event. That event fires on a TRANSITION, and the case that loses a foreman's ticket has no
    // transition in it: he closes the tab in a dead spot, opens the app back at the yard, and the
    // browser is online from the first paint. `online` never fires, so the queue simply sat there.
    // The ten-second poll below updated the COUNT and never processed anything, which made a stuck
    // queue look like a working one.
    //
    // Safe to call from anywhere: `processQueue` no-ops when offline and serialises itself against
    // overlapping runs - its own comment names "online/interval/manual triggers" as the callers it
    // expects. This is the interval it was already written for.
    const drainIfPossible = async (count) => {
      if (!navigator.onLine || count <= 0) return;
      setSyncing(true);
      try {
        await processQueue();
      } finally {
        setSyncing(false);
      }
      const after = await getQueuedTickets();
      setQueuedCount(after.length);
    };

    const checkQueue = async () => {
      const tickets = await getQueuedTickets();
      setQueuedCount(tickets.length);
      // COUNT READ FRESH, not from state. `queuedCount` in this closure is whatever it was when the
      // effect last ran, and a queue written by another tab or a previous session would be judged
      // against a stale zero.
      await drainIfPossible(tickets.length);
    };

    const handleOffline = () => setIsOffline(true);
    const handleOnline = async () => {
      setIsOffline(false);
      await checkQueue();
    };

    const handleSyncComplete = (e) => {
      console.log('Sync complete event received', e.detail);
      checkQueue();
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    window.addEventListener('bfs-sync-complete', handleSyncComplete);
    
    // Check initial queue
    checkQueue();
    // Poll queue every 10 seconds just in case
    const interval = setInterval(checkQueue, 10000);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('bfs-sync-complete', handleSyncComplete);
      clearInterval(interval);
    };
    // NO `queuedCount` DEPENDENCY any more. It was there because `handleOnline` read the count out
    // of state, so the effect had to re-run - which tore down and re-registered every listener and
    // restarted the interval on every count change. Nothing in here reads state now; the count is
    // read fresh from the queue at the moment it is used.
  }, []);

  if (!isOffline && queuedCount === 0 && !syncing) return null;

  return (
    <div className={`fixed top-0 left-0 right-0 z-toast px-4 py-3 flex items-center justify-center gap-3 shadow-md transition-colors ${
      isOffline ? 'bg-orange-600 text-white' : syncing ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'
    }`}>
      {isOffline ? (
        <>
          <WifiOff size={20} className="animate-pulse" />
          <span className="font-bold text-sm tracking-wide">
            OFFLINE MODE ACTIVE
            {queuedCount > 0 && ` • ${queuedCount} Tickets Pending Sync`}
          </span>
        </>
      ) : syncing ? (
        <>
          <RefreshCw size={20} className="animate-spin" />
          <span className="font-bold text-sm tracking-wide">SYNCING OFFLINE DATA...</span>
        </>
      ) : (
        <span className="font-bold text-sm tracking-wide">ALL DATA SYNCED</span>
      )}
    </div>
  );
}
